import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as React from 'react';
import { useId } from 'react';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import { Input } from '../ui/input';
import { useTranslations } from 'next-intl';
import { InviteCreateInput, inviteCreateSchema } from '@/models/schema';
import { useInvites } from '@/hooks/swr/use-invites';
import { toast } from '@/hooks/use-toast';
import { useScheduleUsers } from '@/hooks/swr/use-schedule-users';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function InviteForm({ scheduleId }: { scheduleId: string }) {
  const t = useTranslations('calendar');
  const { invites, add, mutate } = useInvites({ scheduleId });
  const { scheduleUsers, mutate: mutateUsers } = useScheduleUsers({ scheduleId });
  const roleSelectId = useId();

  const form = useForm<InviteCreateInput>({
    resolver: zodResolver(inviteCreateSchema),
    defaultValues: {
      email: '',
      role: 'MEMBER',
      scheduleId,
    },
  });

  async function onSubmit(data: InviteCreateInput) {
    const existing =
      invites.find((invite) => invite.email === data.email) ||
      scheduleUsers.find((user) => user.user.email === data.email);

    if (existing) {
      toast({
        title: t('dialog.invite.alreadyExists'),
        variant: 'destructive',
      });

      return;
    }
    await add({
      ...data,
    });

    await mutate(invites, {
      revalidate: true,
    });

    await mutateUsers(scheduleUsers, {
      revalidate: true,
    });

    toast({
      title: t('dialog.invite.userInvited'),
    });
    form.reset();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="flex-[2]">
                <FormLabel className="text-base">{t('fields.email')}</FormLabel>
                <FormControl>
                  <Input {...field} type="email" className="h-10" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>{t('fields.role')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger
                      id={roleSelectId}
                      className="**:data-desc:hidden w-[200px] text-left"
                    >
                      <SelectValue
                        placeholder={t('placeholders.selectResource')}
                        className="w-full overflow-visible text-left"
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="w-[280px] [&_*[role=option]>span]:end-2 [&_*[role=option]>span]:start-auto [&_*[role=option]]:pe-8 [&_*[role=option]]:ps-2">
                    {['OWNER', 'MEMBER', 'MANAGER_RESOURCE', 'MANAGER_PROJECT'].map((r) => (
                      <SelectItem key={r} value={r}>
                        {t('roles.' + r)}
                        <span className="mt-1 block text-xs text-muted-foreground" data-desc>
                          {t('roleDescriptions.' + r)}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button type="submit" disabled={form.formState.isSubmitting} className="gap-2">
          {form.formState.isSubmitting ? t('fields.sending') : t('fields.send')}
        </Button>
      </form>
    </Form>
  );
}
